import React from "react";
import { Image, View, Dimensions } from "react-native";
import { DEVICE_SIZES, useDeviceSize } from "rn-responsive-styles";

type Props = {
  variant:
  | "full"
  | "upper"
  | "head"
  | "profile"
  | "profileSmall"
  | "profileMedium"
  | "profileLarge"
  | "eventCard"
  | "player"
  | "smallPlayer"
  | "largePlayer"
  | "walkthroughPlayer"
  | "notificationSmallPlayer"
  | "notificationLargePlayer"
  | "pastSmallPlayer"
  | "liveNowPlayer"
  | "liveSmallPlayer"
  | "PlayerDetailPlayer"
  | "OnGoingPlayerDetail"
  | "chatAvatar"
  | "blockUser"
  | "fullForSignUp"
  | "FullForNotification"
  | "event"
  | "participants";
  src: string;
  changeAvatar?: boolean;
  style?: any;
};

const Avatar = ({ variant, src, changeAvatar, style }: Props) => {
  const deviceSize = useDeviceSize();
  const screenWidth = Dimensions.get("window").width;

  const imageActualWidth = 401;
  const imageActualHeight = 500;

  const isSmallDevice =
    deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;

  // Calculate container size for dynamic positioning (matches AvatarLookComponent calculation)
  const containerSize = isSmallDevice ? Math.floor((screenWidth - 80) / 3) : undefined;

  // Scale factor for translateX based on container size relative to base size (100px)
  const scaleFactor = containerSize ? containerSize / 100 : 1;

  const baseStyle = {
    width: isSmallDevice ? 100 : 181,
    height: isSmallDevice ? 100 : 181,
  };

  const imageStyles = {
    full: {
      ...(changeAvatar ? baseStyle : { width: 119, height: 119 }),
      scale: isSmallDevice ? 0.2 : changeAvatar ? 0.362 : 0.23,
      translateX: isSmallDevice ? Math.floor(20 * scaleFactor) : changeAvatar ? 40 : 50,
      translateY: 0,
    },
    fullForSignUp: {
      width: style?.width,
      height: style?.height,
      scale: isSmallDevice ? 0.19 : 0.2,
      translateX: 20,
      translateY: 0,
    },
    FullForNotification: {
      width: isSmallDevice ? 31 : 125,
      height: isSmallDevice ? 31 : 100,
      scale: isSmallDevice ? 0.07 : 0.2,
      translateX: 20,
      translateY: 0,
      // transform: [{ rotateY: "180deg" }],
    },
    chatAvatar: {
      width: 42,
      height: 42,
      scale: 0.15,
      translateX: -63,
      translateY: 0,
    },
    PlayerDetailPlayer: {
      width: 57,
      height: 70,
      scale: 0.15,
      translateX: -25,
      translateY: 0,
    },
    OnGoingPlayerDetail: {
      width: 95,
      height: 75,
      scale: 0.2,
      translateX: 35,
      translateY: 30,
    },
    upper: {
      ...(changeAvatar ? baseStyle : { width: 119, height: 119 }),
      scale: isSmallDevice ? 0.28 : changeAvatar ? 0.51 : 0.37,
      translateX: isSmallDevice ? Math.floor(-25 * scaleFactor) : changeAvatar ? -15 : -45,
      translateY: 0,
    },
    head: {
      ...(changeAvatar ? baseStyle : { width: 119, height: 119 }),
      scale: isSmallDevice ? 0.45 : changeAvatar ? 0.83 : 0.5,
      translateX: isSmallDevice ? Math.floor(-95 * scaleFactor) : changeAvatar ? -85 : -80,
      translateY: changeAvatar ? 0 : 5,
    },
    walkthroughPlayer: {
      height: isSmallDevice ? 91 : 130,
      width: isSmallDevice ? 91 : 120,
      scale: isSmallDevice ? 0.45 : 0.6,
      translateX: -95,
      translateY: 0,
    },
    notificationSmallPlayer: {
      height: 48,
      width: 85,
      scale: 0.2,
      translateX: -27,
      translateY: 0,
    },
    notificationLargePlayer: {
      width: 54,
      height: 54,
      scale: 0.2,
      translateX: -70,
      translateY: 0,
    },
    player: {
      width: 50,
      height: 50,
      scale: 0.1,
      translateX: 0,
      translateY: 0,
    },
    blockUser: {
      width: 50,
      height: 50,
      scale: 0.1,
      translateX: 48,
      translateY: 0,
    },
    smallPlayer: {
      width: 42,
      height: 42,
      scale: 0.08,
      translateX: 0,
      translateY: 0,
    },
    liveSmallPlayer: {
      width: 51,
      height: 51,
      scale: 0.1,
      translateX: 60,
      translateY: 0,
    },
    profile: {
      width: 40,
      height: 40,
      scale: 0.2,
      translateX: -110,
      translateY: 0,
    },
    profileMedium: {
      width: 80,
      height: 80,
      scale: 0.4,
      translateX: -110,
      translateY: 0,
    },
    profileSmall: {
      width: 28,
      height: 28,
      scale: 0.13,
      translateX: -100,
      translateY: 0,
    },
    largePlayer: {
      width: 110,
      height: 128,
      scale: 0.26,
      translateX: isSmallDevice ? 0 : 50,
      translateY: -5,
    },
    profileLarge: {
      width: isSmallDevice ? 91 : 130,
      height: isSmallDevice ? 91 : 130,
      scale: isSmallDevice ? 0.42 : 0.6,
      translateX: isSmallDevice ? -90 : -90,
      translateY: 0,
    },
    eventCard: {
      width: isSmallDevice ? 95 : 112,
      height: isSmallDevice ? 93 : 109,
      scale: isSmallDevice ? 0.28 : 0.33,
      translateX: isSmallDevice ? -30 : -30,
      translateY: 0,
    },
    pastSmallPlayer: {
      width: 82,
      height: 40,
      scale: 0.18,
      translateX: -89,
      translateY: 0,
    },
    liveNowPlayer: {
      width: isSmallDevice ? 73 : 100,
      height: isSmallDevice ? 72 : 95,
      scale: isSmallDevice ? 0.25 : 0.35,
      translateX: isSmallDevice ? -40 : -62,
      translateY: 0,
    },
    event: {
      width: 42,
      height: 42,
      scale: 0.08,
      translateX: 0,
      translateY: 0,
    },
    participants: {
      width: 42,
      height: 42,
      scale: 0.08,
      translateX: 0,
      translateY: 0,
    },
    imageWidth: baseStyle.width,
    imageHeight: baseStyle.height,
  };

  // Get the style for the current variant, with fallback to 'player' if variant doesn't exist
  const getVariantStyle = () => {
    if (imageStyles[variant]) {
      return imageStyles[variant];
    }

    // Fallback to 'player' variant if the requested variant doesn't exist
    console.warn(`Avatar variant "${variant}" not found, using fallback "player" variant`);
    return imageStyles.player;
  };

  const currentStyle = getVariantStyle();

  return (
    <View
      style={{
        height: currentStyle.height,
        width: currentStyle.width,
        overflow: "hidden",
        ...(variant === "blockUser" && {
          borderRadius: 50,
        }),
      }}
    >
      <Image
        source={{ uri: src }}
        style={{
          transform: [
            { scaleX: currentStyle.scale },
            { scaleY: currentStyle.scale },
            { translateX: currentStyle.translateX },
            { translateY: currentStyle.translateY },
          ],
          transformOrigin: "top left",
          height: imageActualHeight,
          width: imageActualWidth,
        }}
      />
    </View>
  );
};

export default Avatar;
